import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  TickMarkType,
  createChart,
  type CandlestickData,
  type IChartApi,
  type IRange,
  type LineData,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { TradingVolumeRecord } from '@/services/tradingVolumeApi';
import type { MarkPriceExchange } from '@/services/markPriceApi';
import type { IndexPriceExchange } from '@/services/indexPriceApi';
import type { PremiumExchange } from '@/services/premiumIndexApi';
import { formatCandleTooltipValue } from '@/lib/chartFormat';

export const DATA_DETAIL_CHART_HEIGHT = 280;
export const EXCHANGE1_COLOR = '#2563eb';
export const EXCHANGE2_COLOR = '#f97316';

export interface ChartSyncGroup {
  charts: Set<IChartApi>;
  range: IRange<Time> | null;
  syncing: boolean;
  suppressUntil: number;
}

export type ChartSyncGroupRef = MutableRefObject<ChartSyncGroup>;

export interface CandleLine {
  key: string;
  label: string;
  color: string;
  data: CandlestickData<UTCTimestamp>[];
}

export interface ValueLine {
  key: string;
  label: string;
  color: string;
  data: LineData<UTCTimestamp>[];
  lineStyle?: LineStyle;
  lineWidth?: 1 | 2 | 3 | 4;
}

export const createChartSyncGroup = (): ChartSyncGroup => ({
  charts: new Set<IChartApi>(),
  range: null,
  syncing: false,
  suppressUntil: 0,
});

const pad2 = (value: number): string => String(value).padStart(2, '0');

const timeToUtcDate = (time: Time): Date => {
  if (typeof time === 'number') return new Date(time * 1000);
  if (typeof time === 'string') return new Date(`${time}T00:00:00Z`);
  return new Date(Date.UTC(time.year, time.month - 1, time.day));
};

const formatUtcDateTime = (time: Time): string => {
  const date = timeToUtcDate(time);
  return [
    date.getUTCFullYear(),
    '-',
    pad2(date.getUTCMonth() + 1),
    '-',
    pad2(date.getUTCDate()),
    ' ',
    pad2(date.getUTCHours()),
    ':',
    pad2(date.getUTCMinutes()),
  ].join('');
};

const formatUtcTick = (time: Time, tickMarkType: TickMarkType): string => {
  const date = timeToUtcDate(time);
  if (tickMarkType === TickMarkType.Year) return String(date.getUTCFullYear());
  if (tickMarkType === TickMarkType.Month) return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
  if (tickMarkType === TickMarkType.DayOfMonth) return `${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
  return `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
};

const toUtcTimestamp = (timestamp: string): UTCTimestamp | null => {
  const value = new Date(timestamp).getTime();
  if (!Number.isFinite(value)) return null;
  return Math.floor(value / 1000) as UTCTimestamp;
};

const sortByTime = <T extends { time: UTCTimestamp }>(data: T[]): T[] =>
  [...data].sort((a, b) => Number(a.time) - Number(b.time));

export const exchangeToSlug = (exchange: string): string => exchange.trim().toLowerCase().replace(/\./g, '');

export const isPremiumExchange = (value: string): value is PremiumExchange =>
  ['bybit', 'okx', 'binance', 'gateio'].includes(value);

export const isMarkExchange = (value: string): value is MarkPriceExchange =>
  ['bybit', 'okx', 'binance', 'gateio', 'bitget'].includes(value);

export const isIndexExchange = (value: string): value is IndexPriceExchange =>
  ['bybit', 'okx', 'binance', 'gateio', 'bitget'].includes(value);

export const isTradingVolumeExchange = (value: string): boolean =>
  ['binance', 'okx', 'bybit', 'gateio', 'bitget', 'hyperliquid'].includes(value);

export const mapMsCandle = (data: Array<{ ts_ms: number; open: number; high: number; low: number; close: number }>) =>
  sortByTime(
    data
      .map((point): CandlestickData<UTCTimestamp> | null => {
        if (!Number.isFinite(point.ts_ms)) return null;
        return {
          time: Math.floor(point.ts_ms / 1000) as UTCTimestamp,
          open: point.open,
          high: point.high,
          low: point.low,
          close: point.close,
        };
      })
      .filter((point): point is CandlestickData<UTCTimestamp> => point != null),
  );

export const mapTradingVolumeQuoteLine = (data: TradingVolumeRecord[]) =>
  sortByTime(
    data
      .map((point): LineData<UTCTimestamp> | null => {
        const time = toUtcTimestamp(point.ts);
        if (time == null || !Number.isFinite(point.volume_quote_24h)) return null;
        return { time, value: point.volume_quote_24h };
      })
      .filter((point): point is LineData<UTCTimestamp> => point != null),
  );

const createTooltip = (container: HTMLDivElement): HTMLDivElement => {
  const tooltip = document.createElement('div');
  tooltip.className = 'overview-tooltip';
  tooltip.style.display = 'none';
  container.appendChild(tooltip);
  return tooltip;
};

const renderTooltip = (
  tooltip: HTMLDivElement,
  title: string,
  rows: Array<{ label: string; value: string; color?: string }>,
) => {
  tooltip.replaceChildren();

  const titleEl = document.createElement('div');
  titleEl.className = 'overview-tooltip-title';
  titleEl.textContent = title;
  tooltip.appendChild(titleEl);

  rows.forEach((row) => {
    const rowEl = document.createElement('div');
    rowEl.className = 'overview-tooltip-row';

    const labelEl = document.createElement('span');
    labelEl.className = 'overview-tooltip-label';
    if (row.color) {
      const dot = document.createElement('i');
      dot.className = 'overview-dot';
      dot.style.background = row.color;
      labelEl.appendChild(dot);
    }
    labelEl.append(document.createTextNode(row.label));

    const valueEl = document.createElement('span');
    valueEl.className = 'overview-tooltip-value';
    valueEl.textContent = row.value;

    rowEl.append(labelEl, valueEl);
    tooltip.appendChild(rowEl);
  });
};

const positionTooltip = (tooltip: HTMLDivElement, container: HTMLDivElement, point: { x: number; y: number }) => {
  const padding = 12;
  tooltip.style.display = 'block';
  const left =
    point.x + padding + tooltip.offsetWidth > container.clientWidth
      ? Math.max(padding, point.x - tooltip.offsetWidth - padding)
      : point.x + padding;
  const top =
    point.y + padding + tooltip.offsetHeight > container.clientHeight
      ? Math.max(padding, point.y - tooltip.offsetHeight - padding)
      : point.y + padding;
  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
};

const bindChartTimeSync = (chart: IChartApi, syncGroupRef: ChartSyncGroupRef) => {
  const group = syncGroupRef.current;
  group.charts.add(chart);

  const currentRange = chart.timeScale().getVisibleRange();
  if (group.range) {
    group.syncing = true;
    group.suppressUntil = performance.now() + 200;
    chart.timeScale().setVisibleRange(group.range);
    window.setTimeout(() => {
      group.syncing = false;
    }, 0);
  } else if (currentRange) {
    group.range = currentRange;
  }

  const handleRangeChange = (range: IRange<Time> | null) => {
    if (!range || group.syncing || performance.now() < group.suppressUntil) return;
    group.range = range;
    group.syncing = true;
    group.suppressUntil = performance.now() + 200;
    group.charts.forEach((otherChart) => {
      if (otherChart === chart) return;
      try {
        otherChart.timeScale().setVisibleRange(range);
      } catch {}
    });
    window.setTimeout(() => {
      group.syncing = false;
    }, 0);
  };

  chart.timeScale().subscribeVisibleTimeRangeChange(handleRangeChange);

  return () => {
    chart.timeScale().unsubscribeVisibleTimeRangeChange(handleRangeChange);
    group.charts.delete(chart);
  };
};

const getSeriesData = <T,>(seriesData: Map<unknown, unknown>, series: unknown): T | undefined =>
  seriesData.get(series) as T | undefined;

export function DataDetailPairCandleChart({
  series,
  syncGroupRef,
}: {
  series: CandleLine[];
  syncGroupRef: ChartSyncGroupRef;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';

    const chart = createChart(container, {
      width: container.clientWidth || 900,
      height: DATA_DETAIL_CHART_HEIGHT,
      layout: { background: { type: ColorType.Solid, color: '#ffffff' }, textColor: '#4b5563' },
      grid: { vertLines: { color: '#eef2f7' }, horzLines: { color: '#eef2f7' } },
      crosshair: { mode: CrosshairMode.Normal },
      localization: { timeFormatter: formatUtcDateTime },
      rightPriceScale: { borderColor: '#e5e7eb' },
      timeScale: {
        borderColor: '#e5e7eb',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: formatUtcTick,
      },
    });

    const tooltipSeries: Array<{ series: unknown; label: string; color: string }> = [];
    series.forEach((line) => {
      if (line.data.length === 0) return;
      const api = chart.addSeries(CandlestickSeries, {
        upColor: line.color,
        downColor: line.color,
        borderColor: line.color,
        wickColor: line.color,
        priceFormat: { type: 'price', precision: 6, minMove: 0.000001 },
      });
      api.setData(line.data);
      tooltipSeries.push({ series: api, label: line.label, color: line.color });
    });

    const tooltip = createTooltip(container);
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time) {
        tooltip.style.display = 'none';
        return;
      }
      const rows = tooltipSeries
        .map((item) => {
          const point = getSeriesData<CandlestickData<Time>>(param.seriesData as Map<unknown, unknown>, item.series);
          if (!point || !('open' in point)) return null;
          return {
            label: item.label,
            value: formatCandleTooltipValue(point),
            color: item.color,
          };
        })
        .filter((row): row is { label: string; value: string; color: string } => row != null);
      if (rows.length === 0) {
        tooltip.style.display = 'none';
        return;
      }
      renderTooltip(tooltip, formatUtcDateTime(param.time), rows);
      positionTooltip(tooltip, container, param.point);
    });

    chart.timeScale().fitContent();
    const unbindSync = bindChartTimeSync(chart, syncGroupRef);
    const resizeObserver = new ResizeObserver(() => chart.applyOptions({ width: container.clientWidth || 900 }));
    resizeObserver.observe(container);

    return () => {
      unbindSync();
      resizeObserver.disconnect();
      chart.remove();
    };
  }, [series, syncGroupRef]);

  return <div className="overview-chart" ref={containerRef} />;
}

export function DataDetailLineChart({
  lines,
  syncGroupRef,
  valueSuffix = '%',
  valuePrecision = 6,
}: {
  lines: ValueLine[];
  syncGroupRef: ChartSyncGroupRef;
  valueSuffix?: string;
  valuePrecision?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';
    const minMove = 10 ** -valuePrecision;

    const chart = createChart(container, {
      width: container.clientWidth || 900,
      height: DATA_DETAIL_CHART_HEIGHT,
      layout: { background: { type: ColorType.Solid, color: '#ffffff' }, textColor: '#4b5563' },
      grid: { vertLines: { color: '#eef2f7' }, horzLines: { color: '#eef2f7' } },
      crosshair: { mode: CrosshairMode.Normal },
      localization: { timeFormatter: formatUtcDateTime },
      rightPriceScale: { borderColor: '#e5e7eb' },
      timeScale: {
        borderColor: '#e5e7eb',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: formatUtcTick,
      },
    });

    const tooltipLines: Array<{ series: unknown; label: string; color: string }> = [];
    lines.forEach((line) => {
      if (line.data.length === 0) return;
      const api = chart.addSeries(LineSeries, {
        color: line.color,
        lineStyle: line.lineStyle ?? LineStyle.Solid,
        lineWidth: line.lineWidth ?? 2,
        priceFormat: { type: 'price', precision: valuePrecision, minMove },
      });
      api.setData(line.data);
      tooltipLines.push({ series: api, label: line.label, color: line.color });
    });

    const tooltip = createTooltip(container);
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time) {
        tooltip.style.display = 'none';
        return;
      }
      const rows = tooltipLines
        .map((item) => {
          const point = getSeriesData<LineData<Time>>(param.seriesData as Map<unknown, unknown>, item.series);
          if (!point || !('value' in point)) return null;
          return { label: item.label, value: `${point.value.toFixed(valuePrecision)}${valueSuffix}`, color: item.color };
        })
        .filter((row): row is { label: string; value: string; color: string } => row != null);
      if (rows.length === 0) {
        tooltip.style.display = 'none';
        return;
      }
      renderTooltip(tooltip, formatUtcDateTime(param.time), rows);
      positionTooltip(tooltip, container, param.point);
    });

    chart.timeScale().fitContent();
    const unbindSync = bindChartTimeSync(chart, syncGroupRef);
    const resizeObserver = new ResizeObserver(() => chart.applyOptions({ width: container.clientWidth || 900 }));
    resizeObserver.observe(container);

    return () => {
      unbindSync();
      resizeObserver.disconnect();
      chart.remove();
    };
  }, [lines, syncGroupRef, valuePrecision, valueSuffix]);

  return <div className="overview-chart" ref={containerRef} />;
}
